/**
 * Periodically spawns coins on walkable cells within the maze.
 *
 * Manages the lifecycle of active coins: creation, timing, and
 * removal (with fade-out animation handled by CoinItem itself).
 */

import Phaser from 'phaser';
import CoinItem from '../entities/CoinItem.js';
import MazeGenerator from './MazeGenerator.js';
import {
  COIN_SPAWN_INTERVAL,
  MAX_ACTIVE_COINS,
  COIN_LIFETIME,
  COIN_NEAR_PLAYER_CHANCE,
  TILE_SIZE,
  MIN_SPAWN_DISTANCE_FROM_PLAYER,
} from '../config/game-config.js';

export default class CoinSpawner {
  /**
   * @param {Phaser.Scene} scene       - The GameScene instance.
   * @param {number[][]}   grid        - The maze grid.
   * @param {Phaser.Physics.Arcade.Group} coinGroup - Physics group for coins.
   * @param {{ col: number, row: number }} playerPos - Reference to current player grid position.
   * @param {{ col: number, row: number }} exitPos   - Exit position to avoid spawning on.
   * @param {Set<string>} [forbiddenCells] - Set of "col,row" keys for hazard tiles (coins must not spawn on water/lava).
   * @param {import('../entities/Player.js').default} [player] - Player entity (used to exclude movement destination cell).
   */
  constructor(scene, grid, coinGroup, playerPos, exitPos, forbiddenCells, player) {
    this.scene = scene;
    this.grid = grid;
    this.coinGroup = coinGroup;
    this.playerPos = playerPos;
    this.exitPos = exitPos;

    /** @type {Set<string>} Hazard tile positions (water + lava) — coins cannot spawn here. */
    this.forbiddenCells = forbiddenCells || new Set();

    /** @type {import('../entities/Player.js').default|null} */
    this.player = player || null;

    /** @type {CoinItem[]} */
    this.activeItems = [];

    /**
     * Sibling spawner item lists — used to prevent coins from spawning
     * on cells already occupied by food, keys, chests, or the compass.
     * Set via {@link setSiblings} after all spawners are created.
     * @type {{ food: Phaser.GameObjects.Sprite[]|null, keys: any[]|null, chests: any[]|null, compassRef: { item: import('../entities/CompassItem.js').default|null }|null }}
     */
    this._siblingFood = null;
    this._siblingKeys = null;
    this._siblingChests = null;
    /** @type {{ item: import('../entities/CompassItem.js').default|null }|null} */
    this._compassRef = null;

    /** @type {Phaser.Time.TimerEvent|null} */
    this.spawnTimer = null;

    /**
     * Multiplier for the maximum number of active coins. Default 1.
     * Used by LevelEventSystem for "Gold Rush" event (3x coins).
     * @type {number}
     */
    this.maxActiveMultiplier = 1;
  }

  // ---------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------

  /**
   * Set references to all sibling spawner item lists so this spawner
   * avoids placing coins on cells already occupied by other items.
   * @param {Phaser.GameObjects.Sprite[]|null} food - FoodSpawner.activeItems
   * @param {any[]|null} keys - KeySpawner.activeItems
   * @param {any[]|null} chests - ChestSpawner.activeItems
   * @param {{ item: import('../entities/CompassItem.js').default|null }|null} compassRef - Object holding reference to the compass item
   */
  setSiblings(food, keys, chests, compassRef, totemRef) {
    this._siblingFood = food;
    this._siblingKeys = keys;
    this._siblingChests = chests;
    this._compassRef = compassRef || null;
    this._totemRef = totemRef || null;
  }

  /** Start the spawn cycle. */
  start() {
    this.spawnTimer = this.scene.time.addEvent({
      delay: COIN_SPAWN_INTERVAL,
      callback: this._trySpawn,
      callbackScope: this,
      loop: true,
    });

    // Spawn one after a short delay (don't compete with first food spawn)
    this._initialDelay = this.scene.time.delayedCall(1500, () => {
      this._initialDelay = null;
      this._trySpawn();
    });
  }

  /** Stop spawning and clean up all active items. */
  stop() {
    if (this._initialDelay) {
      this._initialDelay.destroy();
      this._initialDelay = null;
    }
    if (this.spawnTimer) {
      this.spawnTimer.destroy();
      this.spawnTimer = null;
    }
    this._destroyAllItems();
  }

  /** Called each frame to clean up expired items. */
  update() {
    this.activeItems = this.activeItems.filter((item) => item.active);
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /** @private Attempt to spawn a new coin if below the cap. */
  _trySpawn() {
    // Clean dead references first
    this.activeItems = this.activeItems.filter((item) => item.active);

    if (this.activeItems.length >= MAX_ACTIVE_COINS * this.maxActiveMultiplier) {
      return;
    }

    const cell = this._findSpawnCell();
    if (!cell) {
      return;
    }

    const worldX = cell.col * TILE_SIZE + TILE_SIZE / 2;
    const worldY = cell.row * TILE_SIZE + TILE_SIZE / 2;

    const item = new CoinItem(this.scene, worldX, worldY, COIN_LIFETIME);
    this.coinGroup.add(item);
    this.activeItems.push(item);
  }

  /**
   * Pick a walkable cell not occupied by the player, exit, existing coins,
   * or any sibling item (food, keys, chests, compass).
   *
   * All candidates must be at least MIN_SPAWN_DISTANCE_FROM_PLAYER
   * Manhattan distance from the player so items never appear on or
   * immediately adjacent to the player — even during a movement tween.
   *
   * @private
   * @returns {{ col: number, row: number }|null}
   */
  _findSpawnCell() {
    const pathCells = MazeGenerator.getPathCells(this.grid);

    const occupied = new Set();
    occupied.add(`${this.playerPos.col},${this.playerPos.row}`);
    occupied.add(`${this.exitPos.col},${this.exitPos.row}`);

    // Also exclude the player's movement destination during an active tween
    // so coins never appear on a cell the player is about to land on.
    if (this.player) {
      const dest = this.player.getDestination();
      if (dest) {
        occupied.add(`${dest.col},${dest.row}`);
      }
    }

    for (const item of this.activeItems) {
      if (item.active) {
        const c = Math.floor(item.x / TILE_SIZE);
        const r = Math.floor(item.y / TILE_SIZE);
        occupied.add(`${c},${r}`);
      }
    }

    // Also exclude cells occupied by sibling spawner items (food, keys, chests, compass)
    const siblingLists = [this._siblingFood, this._siblingKeys, this._siblingChests];
    for (const list of siblingLists) {
      if (list) {
        for (const item of list) {
          if (item && item.active) {
            // Use gridCol/gridRow if available (keys, chests), else compute from world coords
            const c = item.gridCol != null ? item.gridCol : Math.floor(item.x / TILE_SIZE);
            const r = item.gridRow != null ? item.gridRow : Math.floor(item.y / TILE_SIZE);
            occupied.add(`${c},${r}`);
          }
        }
      }
    }

    // Exclude compass item cell (if spawned and not yet collected)
    if (this._compassRef && this._compassRef.item && this._compassRef.item.active) {
      const ci = this._compassRef.item;
      const cc = ci.gridCol != null ? ci.gridCol : Math.floor(ci.x / TILE_SIZE);
      const cr = ci.gridRow != null ? ci.gridRow : Math.floor(ci.y / TILE_SIZE);
      occupied.add(`${cc},${cr}`);
    }

    // Exclude Threia Totem item cell (if spawned and not yet collected)
    if (this._totemRef && this._totemRef.item && this._totemRef.item.active) {
      const ti = this._totemRef.item;
      const tc = ti.gridCol != null ? ti.gridCol : Math.floor(ti.x / TILE_SIZE);
      const tr = ti.gridRow != null ? ti.gridRow : Math.floor(ti.y / TILE_SIZE);
      occupied.add(`${tc},${tr}`);
    }

    // Filter available cells: not occupied AND at least MIN_SPAWN_DISTANCE_FROM_PLAYER
    // Manhattan distance from the player. The distance check also prevents spawning
    // on the player's destination cell during a movement tween (dest is always 1 tile
    // from the reported playerPos).
    const pCol = this.playerPos.col;
    const pRow = this.playerPos.row;
    const minDist = MIN_SPAWN_DISTANCE_FROM_PLAYER;

    const available = pathCells.filter((cell) => {
      if (occupied.has(`${cell.col},${cell.row}`)) return false;
      // Never spawn coins on hazard tiles (water/lava)
      if (this.forbiddenCells.has(`${cell.col},${cell.row}`)) return false;
      const dist = Math.abs(cell.col - pCol) + Math.abs(cell.row - pRow);
      return dist >= minDist;
    });

    if (available.length === 0) {
      return null;
    }

    // Try to spawn near the player sometimes
    if (Math.random() < COIN_NEAR_PLAYER_CHANCE) {
      const near = this._findNearPlayerCell(available);
      if (near) return near;
    }

    return Phaser.Utils.Array.GetRandom(available);
  }

  /**
   * Find a walkable cell within a moderate radius of the player.
   *
   * @private
   * @param {{ col: number, row: number }[]} available
   * @returns {{ col: number, row: number }|null}
   */
  _findNearPlayerCell(available) {
    const pCol = this.playerPos.col;
    const pRow = this.playerPos.row;
    const minDist = 3;
    const maxDist = 12;

    const nearby = available.filter((cell) => {
      const dx = cell.col - pCol;
      const dy = cell.row - pRow;
      const dist = Math.abs(dx) + Math.abs(dy);
      return dist >= minDist && dist <= maxDist;
    });

    if (nearby.length === 0) {
      return null;
    }

    return Phaser.Utils.Array.GetRandom(nearby);
  }

  /** @private */
  _destroyAllItems() {
    for (const item of this.activeItems) {
      if (item.active) {
        item.destroy();
      }
    }
    this.activeItems = [];
  }
}
